import { useEffect, useRef } from 'react';
import cx from 'classnames';

import { formatNumberToGBPString } from 'Pages/vehicles/helpers/formatNumberToGBPString';
import { triggerBulkBiddingVehicleRowClicked } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingVehicleRowClicked';
import Datagrid, { DatagridBody, DatagridCell, DatagridHeader, DatagridRow } from 'Storybook/core/Datagrid';
import VehicleData from 'Storybook/peripheral/VehicleData/VehicleData';
import type { VehicleLeadImage } from 'Types/vehicleImage';
import ROUTES from 'Utilities/routes';

import type { ActionableVehicle, UnactionableVehicle } from '../../useFetchVehiclesForBulkBiddingCSV';
import { BULK_BIDDING_DATAGRID_COLUMNS } from '../bulkBiddingModal.const';
import type { FailedBid, SuccessfulBid } from '../useBulkBidding.types';
import type { UseBulkBiddingViewName } from '../useBulkBiddingModalViewName';

import styles from './VehicleTable.module.scss';

const getBidAmount = (
	item: ActionableVehicle | UnactionableVehicle | SuccessfulBid | FailedBid,
): string | number | undefined => {
	if ('bid' in item) {
		return item.bid.value;
	}

	return item.bidAmount;
};

export const VehicleTable: React.FC<{
	datagridBodyClass?: string;
	datagridGridClass?: string;
	modalStage: UseBulkBiddingViewName;
	vehicles: (ActionableVehicle | UnactionableVehicle | SuccessfulBid | FailedBid)[];
}> = ({ datagridBodyClass, datagridGridClass, modalStage, vehicles }) => {
	const bodyRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (bodyRef.current) {
			bodyRef.current.scrollTop = 0;
		}
	}, [vehicles]);

	return (
		<Datagrid colCount={3} rowCount={vehicles.length}>
			<DatagridHeader
				columns={BULK_BIDDING_DATAGRID_COLUMNS}
				gridClass={cx(styles.datagridCustom, datagridGridClass)}
			/>
			<DatagridBody ref={bodyRef} className={cx(styles.datagridBody, datagridBodyClass)}>
				{vehicles.map((item, index) => {
					const bidAmount = getBidAmount(item);
					const { vehicle } = item;
					return (
						<DatagridRow
							key={`${index}-${vehicle.id}`}
							isInteractive
							as={ROUTES.VEHICLE.as(vehicle.id)}
							gridClass={styles.datagridCustom}
							href={ROUTES.VEHICLE.as(vehicle.id)}
							index={index}
							onClick={() => triggerBulkBiddingVehicleRowClicked({ enquiryId: vehicle.id, stage: modalStage })}
							target="_blank"
						>
							<DatagridCell index={0} rowIndex={index}>
								<VehicleData
									cypressId="test"
									leadPhoto={vehicle.leadPhoto as VehicleLeadImage}
									make={vehicle.make}
									model={vehicle.model}
									prettyVrm={vehicle.prettyVrm}
								/>
							</DatagridCell>
							<DatagridCell index={1} rowIndex={index}>
								<div className={styles.datagridCellTextContainer}>{formatNumberToGBPString(vehicle.reservePrice)}</div>
							</DatagridCell>
							<DatagridCell index={2} rowIndex={index}>
								<div
									className={cx(styles.datagridCellTextContainer, {
										[styles.datagridCellTextError]: Boolean('status' in item && item.status === 'failure'),
									})}
								>
									{typeof bidAmount === 'number' ? formatNumberToGBPString(bidAmount) : bidAmount}
								</div>
							</DatagridCell>
						</DatagridRow>
					);
				})}
			</DatagridBody>
		</Datagrid>
	);
};
